import type {
	DiscriminatorSpecFields,
	UnclassifiedExistingClassSuggestion,
	UnclassifiedNewClassSuggestion,
} from "@sitescene/types";

import { useState } from "react";
import { useSearchParams } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import {
	useTaxonomyEditor,
	useTaxonomyWorkspace,
} from "@/features/plausibility/use-taxonomy-editor";
import {
	useResolveUnclassified,
	useUnclassifiedAlert,
} from "@/features/plausibility/use-unclassified";

import { ClassEditor } from "./ClassEditor";
import { DraftBar } from "./DraftBar";
import { SpecEditor } from "./SpecEditor";
import {
	changedFields,
	type ClassRow,
	classRows,
	EMPTY_CLASS,
	EMPTY_SPEC,
	lookAlikeOf,
	SPEC_SOURCE_LABELS,
} from "./taxonomy-view";

type Filter = "alertable" | "all" | "benign" | "inactive" | "problems";

const NEW = "__new__";

/**
 * The verification taxonomy: every class with its description, confusers,
 * costs, and prior, and each alertable class's features. Edits are staged in one
 * shared draft and reach the verifier, for every organization, only when the
 * draft is published. The database is the source of truth; Export writes the
 * in-code seed files a new environment starts from.
 */
export const Taxonomy = () => {
	const workspace = useTaxonomyWorkspace();
	const editor = useTaxonomyEditor();
	// Arriving from an unclassified alert: `new` opens a new class, `edit` an
	// existing one, each prefilled from the alert's suggestion when it has one
	// (the plain Stage 1 name and description otherwise). The alert is resolved
	// once the class is saved.
	const [searchParams, setSearchParams] = useSearchParams();
	const [fromAlert] = useState(() => {
		const alertId = searchParams.get("from") ?? undefined;
		if (searchParams.get("new")) {
			return {
				alertId,
				editClassId: undefined,
				fields: {
					...EMPTY_CLASS,
					alertable: true,
					description: searchParams.get("description") ?? "",
					displayName: searchParams.get("name") ?? "",
				},
			};
		}
		const editClassId = searchParams.get("edit");
		return editClassId && alertId
			? { alertId, editClassId, fields: undefined }
			: undefined;
	});
	const sourceAlert = useUnclassifiedAlert(fromAlert?.alertId);
	const suggestion = sourceAlert.data?.suggestion;
	const newSuggestion =
		fromAlert?.fields && suggestion?.kind === "newClass"
			? suggestion
			: undefined;
	const editSuggestion =
		suggestion?.kind === "existingClass" &&
		suggestion.classId === fromAlert?.editClassId
			? suggestion
			: undefined;
	const resolveAlert = useResolveUnclassified();
	// Set once the alert's class is saved, so its suggestion stops prefilling.
	const [alertResolved, setAlertResolved] = useState(false);
	// The class just created from a suggestion, whose suggested features the
	// spec editor starts from until they are saved.
	const [suggestedSpecFor, setSuggestedSpecFor] = useState<null | string>(null);
	const [selected, setSelected] = useState<null | string>(
		fromAlert?.editClassId ?? (fromAlert ? NEW : null),
	);
	const [filter, setFilter] = useState<Filter>("all");
	const [search, setSearch] = useState("");
	const [published, setPublished] = useState<string>();
	// Alertable as ticked in the open class editor, before it is saved. Keyed
	// to that editor instance, so a save or undo (which remounts it) drops it.
	const [alertableEdit, setAlertableEdit] = useState<null | {
		editorKey: string;
		value: boolean;
	}>(null);

	const data = workspace.data;
	const rows = data ? classRows(data) : [];
	const busy =
		editor.saveClass.isPending ||
		editor.saveSpec.isPending ||
		editor.revertClass.isPending ||
		editor.revertSpec.isPending ||
		editor.publish.isPending ||
		editor.discard.isPending;

	const term = search.trim().toLowerCase();
	const visible = rows.filter((row) => {
		const matches =
			!term ||
			row.classId.includes(term) ||
			row.fields.displayName.toLowerCase().includes(term) ||
			(row.fields.description?.toLowerCase().includes(term) ?? false);
		if (!matches) return false;
		switch (filter) {
			case "alertable":
				return row.fields.active && row.fields.alertable;
			case "benign":
				return row.fields.active && !row.fields.alertable;
			case "inactive":
				return !row.fields.active;
			case "problems":
				return (
					row.issues.length > 0 ||
					(row.fields.active &&
						row.fields.alertable &&
						(row.specSource === "missing" || row.specSource === "inactive"))
				);
			default:
				return true;
		}
	});

	const classOptions = rows.map((row) => ({
		label: `${row.classId} — ${row.fields.displayName}`,
		value: row.classId,
	}));
	const current = rows.find((row) => row.classId === selected);
	const classEditorKey = current
		? `class:${current.classId}:${JSON.stringify(current.fields)}`
		: "";
	const showFeatures = current
		? alertableEdit?.editorKey === classEditorKey
			? alertableEdit.value
			: current.fields.alertable
		: false;
	const referencedBy = current ? lookAlikeOf(rows, current.classId) : [];
	const prefillDescription =
		editSuggestion &&
		!alertResolved &&
		current?.classId === editSuggestion.classId
			? editSuggestion.revisedDescription
			: undefined;
	const prefillSpec =
		newSuggestion && current?.classId === suggestedSpecFor
			? {
					active: true,
					confusers: newSuggestion.fields.confusers,
					features: newSuggestion.features,
				}
			: undefined;

	const resolveFromAlert = (classId: string) => {
		if (!fromAlert?.alertId || alertResolved) return;
		resolveAlert.mutate({ classId, ids: [fromAlert.alertId] });
		setAlertResolved(true);
		setSearchParams({}, { replace: true });
	};

	const columns: Column<ClassRow>[] = [
		{
			header: "Class",
			key: "class",
			render: (row) => (
				<div>
					<code className="text-xs">{row.classId}</code>
					<div className="text-xs text-secondary">{row.fields.displayName}</div>
				</div>
			),
		},
		{
			header: "Kind",
			key: "kind",
			render: (row) => (
				<div className="flex flex-wrap gap-1">
					<Badge
						label={row.fields.alertable ? "alertable" : "benign"}
						value={row.fields.alertable ? "alertNow" : "lowPriorityLog"}
					/>
					{!row.fields.active && <Badge label="inactive" value="skipped" />}
					{row.state !== "live" && (
						<Badge label={`${row.state} in draft`} value="pending" />
					)}
				</div>
			),
		},
		{
			header: "Description",
			key: "description",
			render: (row) => (
				<span
					className={
						row.fields.active && !row.fields.description
							? "text-xs text-error-primary"
							: "text-xs text-secondary"
					}
				>
					{row.fields.description ?? "No description"}
				</span>
			),
		},
		{
			header: "Confusers",
			key: "confusers",
			render: (row) => (
				<span className="text-xs text-secondary">
					{row.fields.confusers.join(", ") || "—"}
				</span>
			),
		},
		{
			header: "Features",
			key: "features",
			render: (row) =>
				row.fields.alertable ? (
					<span
						className={
							row.fields.active &&
							(row.specSource === "missing" || row.specSource === "inactive")
								? "text-xs text-error-primary"
								: "text-xs text-secondary"
						}
					>
						{row.spec?.features.length ?? 0} ·{" "}
						{SPEC_SOURCE_LABELS[row.specSource]}
					</span>
				) : (
					<span className="text-xs text-tertiary">—</span>
				),
		},
		{
			header: "Edited",
			key: "edited",
			render: (row) =>
				row.live?.userEdited ? (
					<span className="text-xs text-secondary">by staff</span>
				) : (
					<span className="text-xs text-tertiary">seed</span>
				),
		},
	];

	const review = (data?.draft?.classes ?? [])
		.map((change) => ({
			key: `class:${change.classId}`,
			label: `${change.classId}: ${changedFields(change.base, change.value).join(", ")}`,
			onRevert: () => editor.revertClass.mutate(change.classId),
		}))
		.concat(
			(data?.draft?.specs ?? []).map((change) => ({
				key: `spec:${change.classId}`,
				label: `${change.classId} features: ${changedFields(change.base, change.value).join(", ")}`,
				onRevert: () => editor.revertSpec.mutate(change.classId),
			})),
		);

	return (
		<section className="flex flex-col gap-4">
			<div className="flex flex-wrap items-start gap-3">
				<div>
					<h2 className="text-base font-semibold text-primary">Taxonomy</h2>
					<p className="text-sm text-secondary">
						Classes, their descriptions, and the features the verifier looks
						for. Shared by every organization. Admin database
						{data?.versions.taxonomy !== undefined &&
							` · taxonomy v${data.versions.taxonomy}, features v${data.versions.discriminators ?? 0}`}
						.
					</p>
				</div>
				<div className="ml-auto flex items-center gap-2">
					<Button
						color="secondary"
						isDisabled={!data?.seeded || editor.exportSeeds.isPending}
						onClick={() => editor.exportSeeds.mutate()}
						size="sm"
						type="button"
					>
						Export seed files
					</Button>
					<Button
						isDisabled={!data}
						onClick={() => setSelected(NEW)}
						size="sm"
						type="button"
					>
						New class
					</Button>
				</div>
			</div>
			{editor.exportSeeds.error && (
				<p className="text-sm text-error-primary" role="alert">
					{editor.exportSeeds.error.message}
				</p>
			)}

			<StatusMessage
				error={workspace.error}
				isLoading={workspace.isLoading}
				loading="Loading taxonomy…"
			/>

			{data && !data.seeded && (
				<p className="text-sm text-warning-primary" role="alert">
					The taxonomy tables have not been seeded, so the verifier is reading
					the in-code seed. Seed them from Global priors before publishing
					changes.
				</p>
			)}

			{sourceAlert.isLoading && (
				<p className="text-sm text-secondary">Loading suggestion…</p>
			)}
			{suggestionNote(
				newSuggestion,
				editSuggestion,
				prefillSpec,
				alertResolved,
			)}

			{data && (
				<DraftBar
					busy={busy}
					confirmMessage="Publish these changes? Every organization's next verification uses them."
					draft={data.draft}
					error={editor.publish.error ?? editor.discard.error}
					issues={data.issues}
					onDiscard={() => editor.discard.mutate()}
					onPublish={() =>
						editor.publish.mutate(undefined, {
							onSuccess: (result) =>
								setPublished(
									`Published ${result.classes} class${result.classes === 1 ? "" : "es"} and ${result.specs} feature spec${result.specs === 1 ? "" : "s"}.`,
								),
						})
					}
					published={published}
					review={review}
				/>
			)}

			{selected === NEW && data && !sourceAlert.isLoading && (
				<section className="rounded-md border border-secondary p-4">
					<div className="mb-3 flex items-center">
						<h3 className="text-sm font-semibold text-primary">New class</h3>
						<Button
							className="ml-auto"
							color="secondary"
							onClick={() => setSelected(null)}
							size="sm"
							type="button"
						>
							Close
						</Button>
					</div>
					<ClassEditor
						busy={busy}
						classOptions={classOptions}
						error={editor.saveClass.error}
						initial={
							newSuggestion && {
								classId: newSuggestion.classId,
								fields: newSuggestion.fields,
							}
						}
						key={newSuggestion ? `new:${newSuggestion.classId}` : "new"}
						onSave={(classId, fields) =>
							editor.saveClass.mutate(
								{ classId, create: true, fields },
								{
									onSuccess: () => {
										setSelected(classId);
										if (newSuggestion?.classId === classId) {
											setSuggestedSpecFor(classId);
										}
										resolveFromAlert(classId);
									},
								},
							)
						}
						staged={false}
						value={fromAlert?.fields ?? EMPTY_CLASS}
					/>
				</section>
			)}

			{/*
			 * Gated on the alert load like the new-class section above: the
			 * suggestion decides this editor's key, so mounting before it
			 * arrives means remounting when it does, discarding whatever was
			 * typed in the meantime. The query is disabled when we did not
			 * arrive from an alert, so this never waits on an ordinary visit.
			 */}
			{current && data && !sourceAlert.isLoading && (
				<section className="flex flex-col gap-4 rounded-md border border-secondary p-4">
					<div className="flex items-center gap-2">
						<h3 className="text-sm font-semibold text-primary">
							{current.fields.displayName || current.classId}
						</h3>
						<code className="text-xs text-tertiary">{current.classId}</code>
						<Button
							className="ml-auto"
							color="secondary"
							onClick={() => setSelected(null)}
							size="sm"
							type="button"
						>
							Close
						</Button>
					</div>
					{current.issues.length > 0 && (
						<ul
							className="list-disc pl-5 text-sm text-error-primary"
							role="alert"
						>
							{current.issues.map((issue) => (
								<li key={issue.message}>{issue.message}</li>
							))}
						</ul>
					)}
					<ClassEditor
						busy={busy}
						classId={current.classId}
						classOptions={classOptions}
						error={editor.saveClass.error ?? editor.revertClass.error}
						initial={
							prefillDescription === undefined
								? undefined
								: {
										fields: {
											...current.fields,
											description: prefillDescription,
										},
									}
						}
						key={`${classEditorKey}${prefillDescription === undefined ? "" : ":suggested"}`}
						onAlertableChange={(value) =>
							setAlertableEdit({ editorKey: classEditorKey, value })
						}
						onRevert={() => editor.revertClass.mutate(current.classId)}
						onSave={(classId, fields) =>
							editor.saveClass.mutate(
								{ classId, create: false, fields },
								{
									onSuccess: () => {
										// Whether or not the suggestion prefilled this editor:
										// staff came here to widen this class for that alert and
										// have now saved it. Keying the resolve to the prefill
										// left the alert pending whenever the suggestion had
										// been regenerated into a different shape, or its fetch
										// failed — silently, since nothing else resolves it.
										if (fromAlert?.editClassId === classId) {
											resolveFromAlert(classId);
										}
									},
								},
							)
						}
						staged={current.state !== "live"}
						value={current.fields}
					/>

					{showFeatures ? (
						<div>
							<h4 className="mb-1 text-sm font-semibold text-primary">
								Features
							</h4>
							<p className="mb-3 text-xs text-secondary">
								What the anchored pass checks, one observation per feature.
								{current.specSource === "seed" &&
									" No spec row yet: the verifier uses the in-code seed. Saving writes a row."}
							</p>
							{!current.fields.alertable && (
								<p className="mb-3 text-xs text-warning-primary" role="status">
									Alertable is not saved yet. Save the class to the draft as
									well, or these features are never read.
								</p>
							)}
							<SpecEditor
								busy={busy}
								classOptions={classOptions.filter(
									(option) => option.value !== current.classId,
								)}
								error={editor.saveSpec.error ?? editor.revertSpec.error}
								initial={prefillSpec}
								key={`spec:${current.classId}:${JSON.stringify(current.spec)}${prefillSpec ? ":suggested" : ""}`}
								lockedKeys={current.lockedKeys}
								onRevert={() => editor.revertSpec.mutate(current.classId)}
								onSave={(fields) =>
									editor.saveSpec.mutate(
										{ classId: current.classId, fields },
										{ onSuccess: () => setSuggestedSpecFor(null) },
									)
								}
								prefix={current.classId}
								staged={current.specState !== "live"}
								value={current.spec ?? EMPTY_SPEC}
							/>
						</div>
					) : (
						<div>
							<h4 className="mb-1 text-sm font-semibold text-primary">
								Features
							</h4>
							<p className="text-xs text-secondary">
								Benign classes have no features: the verifier only checks
								footage for alertable ones, and uses benign classes to name what
								a clip really shows. The cues that tell this class apart belong
								on the alertable classes it is mistaken for, as features whose
								presence points to a look-alike. Tick Alertable to give this
								class features of its own.
							</p>
							{referencedBy.length > 0 ? (
								<div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
									<span className="text-tertiary">Look-alike of:</span>
									{referencedBy.map((row) => (
										<Button
											color="link-color"
											key={row.classId}
											onClick={() => setSelected(row.classId)}
											size="sm"
											type="button"
										>
											{row.fields.displayName || row.classId}
										</Button>
									))}
								</div>
							) : (
								<p className="mt-2 text-xs text-tertiary">
									No alertable class lists it as a look-alike yet.
								</p>
							)}
						</div>
					)}
				</section>
			)}

			{data && (
				<>
					<div className="flex flex-wrap items-end gap-3">
						<Input
							label="Search"
							onChange={setSearch}
							placeholder="id, name, or description"
							value={search}
						/>
						<NativeSelect
							label="Show"
							onChange={(event) => setFilter(event.target.value as Filter)}
							options={[
								{ label: "all classes", value: "all" },
								{ label: "alertable", value: "alertable" },
								{ label: "benign", value: "benign" },
								{ label: "inactive", value: "inactive" },
								{ label: "needs attention", value: "problems" },
							]}
							value={filter}
						/>
						<span className="pb-1 text-xs text-tertiary">
							{visible.length} of {rows.length}
						</span>
					</div>
					<DataTable
						columns={columns}
						onRowClick={(row) => setSelected(row.classId)}
						rowKey={(row) => row.classId}
						rows={visible}
					/>
				</>
			)}
		</section>
	);
};

/** What the reviewer is looking at when a suggestion prefilled the editor. */
function suggestionNote(
	newSuggestion: UnclassifiedNewClassSuggestion | undefined,
	editSuggestion: UnclassifiedExistingClassSuggestion | undefined,
	prefillSpec: DiscriminatorSpecFields | undefined,
	alertResolved: boolean,
) {
	const reasoning = (newSuggestion ?? editSuggestion)?.reasoning;
	let step: string | undefined;
	if (newSuggestion && !alertResolved) {
		step =
			"The new class below is prefilled from it. Review every field and add it to the draft; its suggested features open next.";
	} else if (prefillSpec) {
		step =
			"The features below are suggested. Review each key and description, then save them to the draft.";
	} else if (editSuggestion && !alertResolved) {
		step = `The description of ${editSuggestion.classId} below is the suggested revision. Review it and save the class to the draft.`;
	}
	if (!step) return null;
	return (
		<div
			className="rounded-md border border-secondary p-3 text-sm text-secondary"
			role="status"
		>
			<span className="font-medium text-primary">
				Suggested from an unclassified alert.
			</span>{" "}
			{reasoning && `${reasoning} `}
			{step}
		</div>
	);
}
